import React, { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CalendarDays, ClipboardList, TrendingUp } from 'lucide-react';
import WorkspaceMenu from '../../components/WorkspaceMenu';
import { WorkspaceLayout, SidebarTrigger, type SidebarGroup } from '../../components/workspaceSidebar';
import { ViewTransition } from '../../components/shadcn/motion';
import { LeadReportRow, ReportCatalog, ReportData, ReportTemplate, ReportingResult, SalesReportRow, reportingApi } from '../../services/reportingApi';
import DailyOutcomesReport from './DailyOutcomesReport';
import PaPerformanceReport from './PaPerformanceReport';
import './reporting.css';

type LoadState = 'loading' | 'ready' | 'empty' | 'refused' | 'error';
type SendState = 'idle' | 'sending' | 'accepted' | 'failed';

const ZONE = 'America/New_York';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function todayInZone(): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const part = (type: string) => parts.find((item) => item.type === type)?.value || '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

function monthStart(date: string): string {
  return `${date.slice(0, 7)}-01`;
}

function isDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

/**
 * The server's own sentence for a refusal the person can act on (REPORT_*: a bad
 * range, too many rows, an invalid recipient, a report too large to email). These
 * reach us intact through the global error handler; anything else gets our copy.
 */
function actionableMessage(result: ReportingResult<unknown>): string | null {
  if (!result.status || result.status >= 500) return null;
  if (!/^REPORT_/.test(result.code || '')) return null;
  return result.message && result.message !== 'Request failed' ? result.message : null;
}

function isRefusal(result: ReportingResult<unknown>): boolean {
  return result.status === 401 || result.status === 403 || /^(AUTHZ_|DENY_)/.test(result.code || '');
}

function display(value: unknown): string {
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'None recorded';
  if (value === null || value === undefined || value === '') return 'Not recorded';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

/** A date with where it came from: a Monday-reported date is never shown as an eMerge fact. */
function dated(value: string | null, source: string | null): string {
  if (!value) return 'Not recorded';
  if (source === 'SOURCE_REPORTED') return `${value} (source reported)`;
  return value;
}

const CLOSE_SOURCE_LABEL: Record<string, string> = {
  EMERGE_STATUS_ACT: 'eMerge status change',
  EMERGE_INTAKE_RULE: 'eMerge intake rule',
  EMERGE_DUPLICATE_CLOSE: 'eMerge duplicate close (rule)',
  EMERGE_ALREADY_SOLD_CLOSE: 'eMerge already-sold close (rule)',
  EMERGE_RULE: 'eMerge rule',
  SOURCE_REPORTED: 'Monday date (before adoption)',
};

const REVERSAL_LABEL: Record<string, string> = {
  SALE_ROW_REVERSED: 'Sale reversed',
  UMED_CLAIM_UNWOUND: 'UMED claim unwound',
};

function money(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return 'Not recorded';
  const amount = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(amount)
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
    : String(value);
}

function Summary({ report }: { report: ReportData }) {
  const summary = report.summary || {};
  const metrics = Object.entries(summary).map(([key, value]) => ({
    label: key === 'sale_value' ? 'Gross sale value' : key === 'sales_closed' ? 'Sales closed (includes reversals)' : key.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()),
    // amount_pending is a COUNT of sales with no amount yet, not money.
    value: key === 'sale_value' ? money(value) : display(value),
  }));

  return (
    <section className="report-section" aria-labelledby="report-summary-heading">
      <div className="report-section-heading">
        <div>
          <p className="report-eyebrow">Aggregate</p>
          <h2 id="report-summary-heading">Report summary</h2>
        </div>
        <p className="report-scope">{report.scope === 'TEAM' ? 'Team-wide' : 'Your scope'} · {report.timezone}</p>
      </div>
      {metrics.length > 0 ? (
        <dl className="report-metrics">
          {metrics.map((metric) => (
            <div className="report-metric" key={metric.label}>
              <dt>{metric.label}</dt>
              <dd>{metric.value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="report-muted">No aggregate totals were provided for this report.</p>
      )}
      <div className="report-device-note" role="note">
        <strong>Physical device units: unavailable.</strong>{' '}
        {report.unit_notice || 'No authoritative source is available for physical device units.'}
      </div>
      {report.template === 'sales' && <p className="report-muted">Closed-sale totals include reversed sales, as Monthly Sales does; reversals (a reversed sale, or a UMED claim unwound) are counted separately.</p>}
    </section>
  );
}

function LeadTable({ rows }: { rows: LeadReportRow[] }) {
  return (
    <>
      <div className="report-table-scroll" role="region" aria-label="PA lead report details" tabIndex={0}>
        <table className="report-table">
          <caption>PA cases first assigned or closed in the date range</caption>
          <thead><tr>
            <th scope="col">Resident</th><th scope="col">Facility</th><th scope="col">Current advocate</th>
            <th scope="col">First assigned</th><th scope="col">Stage</th><th scope="col">Status</th><th scope="col">Disposition</th>
            <th scope="col">Closed</th><th scope="col">Closed by</th><th scope="col">Current tags</th>
          </tr></thead>
          <tbody>{rows.map((row, index) => (
            <tr key={`${row.resident || 'lead'}-${row.facility || ''}-${index}`}>
              <th scope="row">{display(row.resident)}</th><td>{display(row.facility)}</td><td>{display(row.advocate)}</td>
              <td>{dated(row.assigned_on, row.assignment_confidence)}</td><td>{display(row.stage)}</td><td>{display(row.status)}</td>
              <td>{display(row.disposition)}</td><td>{dated(row.closed_on, row.close_date_source)}</td>
              <td>{row.close_date_source ? CLOSE_SOURCE_LABEL[row.close_date_source] || row.close_date_source : 'Not closed'}</td>
              <td>{display(row.tags)}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      <p className="report-muted">Each row is the eMerge PA case. A date marked “source reported” is the Monday board’s, carried in before the case was adopted; it is not an eMerge act. Current resident tags are shown; historical tag values for the selected dates are not available.</p>
    </>
  );
}

function SalesTable({ rows }: { rows: SalesReportRow[] }) {
  return (
    <div className="report-table-scroll" role="region" aria-label="Sales report details" tabIndex={0}>
      <table className="report-table">
        <caption>Sales resident and facility details</caption>
        <thead><tr>
          <th scope="col">Resident</th><th scope="col">Facility</th><th scope="col">Credited advocate</th><th scope="col">Sale closed</th>
          <th scope="col">Sale amount</th><th scope="col">Amount source</th><th scope="col">First assigned</th><th scope="col">Assignment origin</th>
          <th scope="col">Enrollment</th><th scope="col">UMED stage</th><th scope="col">Finance handoff</th><th scope="col">Reversal</th><th scope="col">Sale stands</th>
        </tr></thead>
        <tbody>{rows.map((row, index) => (
          <tr key={`${row.resident || 'sale'}-${row.facility || ''}-${index}`}>
            <th scope="row">{display(row.resident)}</th><td>{display(row.facility)}</td><td>{display(row.advocate)}</td>
            <td>{dated(row.sale_closed_on, row.sale_close_confidence)}</td><td>{money(row.sale_amount)}</td><td>{display(row.amount_source)}</td>
            <td>{dated(row.assigned_on, row.assignment_confidence)}</td><td>{display(row.assignment_origin)}</td>
            <td>{display(row.enrollment_state)}</td><td>{display(row.umed_stage)}</td><td>{display(row.finance_handoff_state)}</td>
            <td>{row.reversal_basis ? REVERSAL_LABEL[row.reversal_basis] || row.reversal_basis : 'None'}</td><td>{display(row.sale_stands)}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function DailyTotals({ report }: { report: ReportData }) {
  return (
    <section className="report-section" aria-labelledby="daily-totals-heading">
      <div className="report-section-heading">
        <div><p className="report-eyebrow">By close date</p><h2 id="daily-totals-heading">Daily sales totals</h2></div>
      </div>
      {report.daily_totals.length === 0 ? (
        <p className="report-empty-inline">No daily totals were returned for this date range.</p>
      ) : (
        <div className="report-table-scroll" role="region" aria-label="Daily sales totals" tabIndex={0}>
          <table className="report-table report-daily-table">
            <caption>Sales totals by date from the reporting service</caption>
            <thead><tr><th scope="col">Date</th><th scope="col">Sales closed (includes reversals)</th><th scope="col">Gross sale value</th><th scope="col">Reversed sales</th></tr></thead>
            <tbody>{report.daily_totals.map((total) => (
              <tr key={total.date}><th scope="row">{total.date}</th><td>{total.sales_closed}</td><td>{money(total.sale_value)}</td><td>{total.reversed}</td></tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** The PA lead and sales reports (2026-10-11), unchanged in behaviour. */
function PaReports() {
  const today = todayInZone();
  const [template, setTemplate] = useState<ReportTemplate>('pa-leads');
  const [from, setFrom] = useState(monthStart(today));
  const [to, setTo] = useState(today);
  const [report, setReport] = useState<ReportData | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [loadMessage, setLoadMessage] = useState('');
  const [recipient, setRecipient] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [confirmedRecipient, setConfirmedRecipient] = useState('');
  const [sendState, setSendState] = useState<SendState>('idle');
  const [sendMessage, setSendMessage] = useState('');
  const requestId = useRef(0);
  const dateError = !isDate(from) || !isDate(to) || from > to;
  const reportMatchesFilters = report?.template === template && report.from === from && report.to === to;

  const loadReport = useCallback(async () => {
    if (!isDate(from) || !isDate(to) || from > to) {
      setLoadState('error');
      setLoadMessage('Choose valid dates with the start date on or before the end date.');
      return;
    }
    const id = ++requestId.current;
    setLoadState('loading');
    setLoadMessage('');
    setReport(null);
    setSendState('idle');
    setSendMessage('');
    const result = await reportingApi.get(template, from, to);
    if (id !== requestId.current) return;
    if (result.success && result.data) {
      setReport(result.data);
      setLoadState(result.data.rows.length === 0 ? 'empty' : 'ready');
      return;
    }
    setLoadMessage(actionableMessage(result) || (isRefusal(result)
      ? 'The server declined access to this report. Your access has not changed.'
      : result.status === undefined
        ? 'Could not reach the reporting service. No report was loaded.'
        : 'The reporting service could not load this report.'));
    setLoadState(isRefusal(result) ? 'refused' : 'error');
  }, [from, template, to]);

  useEffect(() => {
    void loadReport();
  }, [loadReport]);

  const updateFilters = (update: () => void) => {
    update();
    setConfirmed(false);
    setConfirmedRecipient('');
    setSendState('idle');
    setSendMessage('');
  };

  const sendEmail = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!reportMatchesFilters || !report?.content_hash || !EMAIL_RE.test(recipient.trim()) || !confirmed || sendState === 'sending') return;
    setSendState('sending');
    setSendMessage('');
    const result = await reportingApi.email({ template, from, to, recipient: recipient.trim(), acknowledged_recipient: confirmedRecipient, report_hash: report.content_hash });
    if (result.success && result.data?.status === 'PROVIDER_ACCEPTED') {
      setSendState('accepted');
      setSendMessage(result.data.audit_status === 'UPDATE_PENDING'
        ? `The email provider accepted the report for ${recipient.trim()} (${result.data.row_count} rows), but the audit status update is pending review.`
        : `The email provider accepted the report for ${recipient.trim()} (${result.data.row_count} rows).`);
      setConfirmed(false);
      return;
    }
    setSendState('failed');
    setSendMessage(actionableMessage(result) || (result.code === 'EMAIL_DISABLED'
      ? 'Email delivery is disabled in development and test. Nothing was sent.'
      : isRefusal(result)
      ? 'The server declined the email request. No report was sent.'
      : result.status === undefined
        ? 'Could not reach the email service. The report was not confirmed as sent.'
        : 'The email service did not accept the report. It was not confirmed as sent.'));
  };

  const rows = report?.rows || [];
  const leadRows = template === 'pa-leads' ? rows as LeadReportRow[] : [];
  const salesRows = template === 'sales' ? rows as SalesReportRow[] : [];
  const canSend = Boolean(reportMatchesFilters && EMAIL_RE.test(recipient.trim()) && confirmed && sendState !== 'sending');

  return (
    <>
      <section className="report-controls" aria-labelledby="report-controls-heading">
        <h2 id="report-controls-heading">Report and date range</h2>
        <div className="report-control-grid">
          <fieldset className="report-template-control">
            <legend>Report</legend>
            <div className="report-segmented" role="group" aria-label="Report template">
              <button type="button" aria-pressed={template === 'pa-leads'} onClick={() => updateFilters(() => setTemplate('pa-leads'))}>PA leads</button>
              <button type="button" aria-pressed={template === 'sales'} onClick={() => updateFilters(() => setTemplate('sales'))}>Sales</button>
            </div>
          </fieldset>
          <label className="report-date-field">From
            <input type="date" value={from} onChange={(event) => updateFilters(() => setFrom(event.target.value))} />
          </label>
          <label className="report-date-field">To
            <input type="date" value={to} onChange={(event) => updateFilters(() => setTo(event.target.value))} />
          </label>
          <button type="button" className="report-primary" onClick={() => void loadReport()} disabled={loadState === 'loading' || dateError}>
            {loadState === 'loading' ? 'Loading report…' : 'Run report'}
          </button>
        </div>
        {dateError && <p className="report-inline-error" role="alert">Choose valid dates with the start date on or before the end date.</p>}
      </section>

      {(loadState === 'loading' || loadState === 'error' || loadState === 'refused') && (
        <section className={`report-state report-state-${loadState}`} aria-live="polite" aria-busy={loadState === 'loading'}>
          {loadState === 'loading' ? <p role="status">Loading report…</p> : <>
            <h2>{loadState === 'refused' ? 'Report access declined' : 'Report unavailable'}</h2>
            <p>{loadMessage}</p>
            <button type="button" className="report-secondary" onClick={() => void loadReport()}>Try again</button>
          </>}
        </section>
      )}

      {(loadState === 'ready' || loadState === 'empty') && report && reportMatchesFilters && (
        <>
          <Summary report={report} />
          {template === 'sales' && <DailyTotals report={report} />}
          <section className="report-section" aria-labelledby="report-details-heading">
            <div className="report-section-heading">
              <div><p className="report-eyebrow">Resident and facility detail</p><h2 id="report-details-heading">{template === 'pa-leads' ? 'PA leads' : 'Sales'}</h2></div>
              <p className="report-generated">Generated {report.generated_at} · {rows.length} rows returned</p>
            </div>
            {rows.length === 0 ? <p className="report-empty-inline">No rows were returned for this date range.</p>
              : template === 'pa-leads' ? <LeadTable rows={leadRows} /> : <SalesTable rows={salesRows} />}
          </section>

          <section className="report-section report-email" aria-labelledby="report-email-heading">
            <div className="report-section-heading">
              <div><p className="report-eyebrow">Optional delivery</p><h2 id="report-email-heading">Email this report</h2></div>
            </div>
            <form onSubmit={sendEmail}>
              <label className="report-recipient">Exact email recipient
                <input type="email" autoComplete="email" value={recipient} onChange={(event) => {
                  setRecipient(event.target.value);
                  setConfirmed(false);
                  setConfirmedRecipient('');
                  setSendState('idle');
                  setSendMessage('');
                }} placeholder="name@example.com" required />
              </label>
              <div className="report-disclosure">
                <strong>This attachment includes resident-identifiable information.</strong>
                <p>It will be sent to this exact recipient: <bdi>{recipient.trim() || 'Enter an email address above'}</bdi>.</p>
                <label className="report-acknowledgement">
                  <input type="checkbox" checked={confirmed} onChange={(event) => {
                    const checked = event.target.checked;
                    setConfirmed(checked);
                    setConfirmedRecipient(checked ? recipient.trim() : '');
                  }} />
                  <span>I verified this exact recipient and am authorized to send this report with resident-identifiable information.</span>
                </label>
              </div>
              <div className="report-send-actions">
                <button type="submit" className="report-primary" disabled={!canSend}>{sendState === 'sending' ? 'Sending…' : 'Send report'}</button>
                {sendState === 'accepted' && <p className="report-send-success" role="status">{sendMessage}</p>}
                {sendState === 'failed' && <p className="report-inline-error" role="alert">{sendMessage}</p>}
              </div>
            </form>
          </section>
        </>
      )}
    </>
  );
}

type ReportView = 'pa' | 'daily' | 'performance';
const VIEW_PARAM = 'view';

/** The view the link asks for (?view=), read from the router's location so a sidebar link drives it. */
function viewFromSearch(search: string): ReportView | null {
  try {
    const v = new URLSearchParams(search).get(VIEW_PARAM);
    return v === 'daily' ? 'daily' : v === 'pa' ? 'pa' : v === 'performance' ? 'performance' : null;
  } catch { return null; }
}

/** The link to a view: this page with ?view= set, any other parameter kept. */
function viewHref(pathname: string, search: string, view: ReportView): string {
  const params = new URLSearchParams(search);
  params.set(VIEW_PARAM, view);
  return `${pathname}?${params.toString()}`;
}

const VIEW_LABEL: Record<ReportView, string> = { pa: 'PA leads & sales', daily: 'Daily approvals & reversals', performance: 'PA performance' };
const VIEW_ICON = { pa: ClipboardList, daily: CalendarDays, performance: TrendingUp } as const;

const INTRO: Record<ReportView, string> = {
  pa: 'PA case and sales reports within your authorized scope: your own cases and sales, or the team’s if you hold team reporting.',
  daily: 'Approvals and reversals by day, the eMerge home of the Monday admin dashboard counts. Team-wide and read-only; open any day to see the cases behind it.',
  performance: 'How the Patient Advocate team is doing against the month or period before it: leads, workable leads and sales. Team-wide and read-only; counts and PA names only.',
};

/**
 * Reporting: the reports the caller may open (GET /emerge/reports/catalog), one at a time. A report the caller
 * cannot open is not offered, so nobody is sent to a refusal. If the catalog cannot be read, all are offered and
 * each report's own route still decides. A catalog from a server that predates PA performance has no flag for it,
 * which reads as "not offered".
 */
const VIEW_ORDER: ReportView[] = ['pa', 'daily', 'performance'];
const offered = (c: ReportCatalog, v: ReportView): boolean => (v === 'pa' ? c.pa_reports : v === 'daily' ? c.daily_outcomes : Boolean(c.pa_performance));

export default function ReportingPage() {
  const [catalog, setCatalog] = useState<ReportCatalog | null>(null);
  const location = useLocation();

  useEffect(() => {
    let live = true;
    void reportingApi.catalog().then((result) => {
      if (!live) return;
      const c = result.success && result.data ? result.data : { pa_reports: true, daily_outcomes: true, pa_performance: true };
      setCatalog(c);
    }, () => {
      if (!live) return;
      setCatalog({ pa_reports: true, daily_outcomes: true, pa_performance: true });
    });
    return () => { live = false; };
  }, []);

  // The link's view when the catalog offers it, otherwise the first report offered (PA leads & sales when it is).
  const available = catalog ? VIEW_ORDER.filter((v) => offered(catalog, v)) : [];
  const wanted = viewFromSearch(location.search);
  const view: ReportView | null = catalog ? (wanted && offered(catalog, wanted) ? wanted : available[0] || null) : null;
  const groups: SidebarGroup[] = [{
    key: 'reports', label: 'Reports',
    items: available.map((v) => ({
      key: v, label: VIEW_LABEL[v], icon: VIEW_ICON[v], href: viewHref(location.pathname, location.search, v), active: view === v, replace: true,
    })),
  }];

  return (
    <div className="report-workspace">
      <WorkspaceMenu current="REPORTING" />
      <WorkspaceLayout workspace="REPORTING" navLabel="Report views" groups={groups}>
        <main className="report-main w-full">
          <header className="report-header">
            <div className="wsb-title">
              <SidebarTrigger />
              <div>
                <p className="report-eyebrow">eMerge · Shared workspace</p>
                <h1>Reporting</h1>
                <p className="report-intro">{INTRO[view || 'pa']}</p>
              </div>
            </div>
          </header>

          <ViewTransition viewKey={catalog ? (view || 'none') : 'loading'}>
            {!catalog ? (
              <section className="report-state report-state-loading" aria-busy="true"><p role="status">Loading reports…</p></section>
            ) : view === null ? (
              <section className="report-state report-state-refused">
                <h2>No reports available</h2>
                <p>Your account holds no reporting access. Your access has not changed; ask your manager if you need a report.</p>
              </section>
            ) : view === 'daily' ? <DailyOutcomesReport /> : view === 'performance' ? <PaPerformanceReport /> : <PaReports />}
          </ViewTransition>
        </main>
      </WorkspaceLayout>
    </div>
  );
}